import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarSolicitudes } from '../../services/adminSolicitudes';
import AppShell from '../../components/layout/AppShell';
import Button from '../../components/ui/Button';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';

const FILTROS = [
  { value: 'PENDIENTE', label: 'Pendientes' },
  { value: 'APROBADA', label: 'Aprobadas' },
  { value: 'RECHAZADA', label: 'Rechazadas' },
  { value: 'TODAS', label: 'Todas' },
];

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

function SolicitudesPage() {
  const navigate = useNavigate();
  const [estado, setEstado] = useState('PENDIENTE');
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async (estadoActual) => {
    setLoading(true);
    setError('');
    const result = await listarSolicitudes(estadoActual);
    if (result.ok) {
      setSolicitudes(result.solicitudes);
    } else {
      setError(result.message);
      setSolicitudes([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    cargar(estado);
  }, [estado, cargar]);

  return (
    <AppShell title="Solicitudes">
      <div className="flex flex-col gap-6">
        {/* -mx-4/px-4 deja que el scroll horizontal de los filtros llegue
            hasta el borde en móvil, sin generar scroll horizontal de página. */}
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 whitespace-nowrap">
            {FILTROS.map((filtro) => (
              <button
                key={filtro.value}
                type="button"
                onClick={() => setEstado(filtro.value)}
                className={`min-h-[40px] shrink-0 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ${
                  estado === filtro.value
                    ? 'bg-green-600 text-white'
                    : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {filtro.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{error}</p>}

        {loading ? (
          <p className="text-center text-sm text-gray-500">Cargando...</p>
        ) : solicitudes.length === 0 ? (
          <EmptyState message="No hay solicitudes en este estado." />
        ) : (
          <>
            {/* Tabla en desktop */}
            <div className="hidden overflow-hidden rounded-2xl border border-gray-100 bg-white lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Nombre</th>
                    <th className="px-4 py-3 font-medium">Correo</th>
                    <th className="px-4 py-3 font-medium">Fecha</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {solicitudes.map((solicitud) => (
                    <tr key={solicitud.id}>
                      <td className="px-4 py-3 font-medium text-gray-900">{solicitud.nombre}</td>
                      <td className="px-4 py-3 text-gray-600">{solicitud.email}</td>
                      <td className="px-4 py-3 text-gray-500">{formatFecha(solicitud.created_at)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge estado={solicitud.estado} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/solicitudes/${solicitud.id}`)}
                          className="font-medium text-green-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                        >
                          Revisar →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tarjetas en móvil/tablet */}
            <div className="flex flex-col gap-3 lg:hidden">
              {solicitudes.map((solicitud) => (
                <div key={solicitud.id} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-900">{solicitud.nombre}</p>
                      <p className="mt-1 truncate text-sm text-gray-500">{solicitud.email}</p>
                      <p className="mt-1 text-xs text-gray-400">{formatFecha(solicitud.created_at)}</p>
                    </div>
                    <StatusBadge estado={solicitud.estado} />
                  </div>

                  <Button
                    variant="secondary"
                    className="mt-3"
                    onClick={() => navigate(`/admin/solicitudes/${solicitud.id}`)}
                  >
                    Revisar
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

export default SolicitudesPage;
